import { EmptyState, EmptyStateSettings } from '@bp/ui-components';
import {
  PartialKeys,
  Virtualizer as TanstackVirtualizer,
  useVirtualizer,
  VirtualizerOptions,
} from '@tanstack/react-virtual';
import { ReactNode, useRef } from 'react';

export type VirtualizerProps<TScrollElement extends Element, TItemElement extends Element> = {
  children: (virtualizer: TanstackVirtualizer<TScrollElement, TItemElement>) => ReactNode;
  options: PartialKeys<
    VirtualizerOptions<TScrollElement, TItemElement>,
    'observeElementRect' | 'observeElementOffset' | 'scrollToFn'
  >;
  actions?: ReactNode;
  emptyStateSettings?: EmptyStateSettings;
};

export const Virtualizer = <TScrollElement extends Element, TItemElement extends Element>({
  children,
  options,
  actions,
  emptyStateSettings,
}: VirtualizerProps<TScrollElement, TItemElement>) => {
  const virtualizer = useVirtualizer<TScrollElement, TItemElement>(options);
  const items = virtualizer.getVirtualItems();

  const ref = useRef<HTMLDivElement | null>(null);

  return items.length === 0 ? (
    <EmptyState {...emptyStateSettings} />
  ) : (
    <div
      style={{
        height: `${virtualizer.getTotalSize() + (ref.current?.getBoundingClientRect().height ?? 0)}px`,
        width: '100%',
        position: 'relative',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          transform: `translateY(${items[0]?.start ?? 0}px)`,
        }}
      >
        <div ref={ref}>{actions}</div>
        {children(virtualizer)}
      </div>
    </div>
  );
};
